import { useEffect, useState } from 'react';
import { isBelowMinimum, limitToCents } from '../../lib/amount';
import { formatMoney } from '../../lib/datetime';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import commitmentsService from '../../services/commitments.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { COMMITMENT_DOW_LABEL } from '../../lib/commitments.helpers';
import {
  defaultPlanForm,
  draftBody,
  planProblem,
  REVIEW_REQUIRED_CODE,
  type PlanForm,
} from '../../lib/contributionPlan.helpers';
import { colors } from '../../styles/theme';
import RequestCallButton from './RequestCallButton';
import ExecutiveRefusal from './ExecutiveRefusal';
import { CALL_CONTEXT, channelsToExecutive } from '../../lib/executiveRefusal';
import type { ActiveCommitmentPolicy, Cadence } from '../../types/commitments';
import type { FundableProduct } from '../../types/finance';

/**
 * A client sets up their own contribution plan (self-service, the primary
 * path). The plan is a commitment with its own short contract: once created,
 * the client is taken straight to sign it. A plan outside the client's declared
 * profile is referred to their Account Executive, and the form offers to
 * request that call rather than leaving a dead end.
 */

const CADENCE_LABEL: Record<Cadence, string> = {
  monthly: 'Every month',
  weekly: 'Every week',
  one_time: 'One time',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.ink,
  fontSize: 14,
};

function Field({ label, children }: Readonly<{ label: string; children: React.ReactNode }>): JSX.Element {
  return (
    <label className="block text-xs font-semibold" style={{ color: colors.muted }}>
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Notice({ tone, children }: Readonly<{ tone: 'info' | 'danger'; children: React.ReactNode }>): JSX.Element {
  const palette = tone === 'danger'
    ? { bg: colors.dangerBg, fg: colors.danger, border: colors.dangerBorder }
    : { bg: colors.infoBg, fg: colors.infoText, border: colors.infoBorder };
  return (
    <div
      className="rounded-lg px-3 py-2 text-sm"
      role={tone === 'danger' ? 'alert' : 'note'}
      style={{ background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}` }}
    >
      {children}
    </div>
  );
}

function ReviewRequiredNotice({ productId }: Readonly<{ productId: string }>): JSX.Element {
  return (
    <Notice tone="info">
      <p>
        This plan needs a quick review with your Account Executive before it can start.
      </p>
      <RequestCallButton productId={productId} context="Contribution plan review" />
    </Notice>
  );
}

function ScheduleFields({
  policy,
  form,
  onChange,
}: Readonly<{
  policy: ActiveCommitmentPolicy;
  form: PlanForm;
  onChange: (patch: Partial<PlanForm>) => void;
}>): JSX.Element {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="How often">
        <select
          value={form.cadence}
          onChange={(e) => onChange({ cadence: e.target.value as Cadence })}
          style={inputStyle}
        >
          {policy.allowed_cadences.map((c) => (
            <option key={c} value={c}>{CADENCE_LABEL[c]}</option>
          ))}
        </select>
      </Field>
      <Field label={form.cadence === 'one_time' ? 'Date' : 'Starting on'}>
        <input
          type="date"
          value={form.startDate}
          onChange={(e) => onChange({ startDate: e.target.value })}
          style={inputStyle}
        />
      </Field>
      {form.cadence === 'monthly' && (
        <Field label="Day of the month">
          <input
            type="number"
            min={1}
            max={28}
            value={form.dayOfMonth}
            onChange={(e) => onChange({ dayOfMonth: e.target.value })}
            style={inputStyle}
          />
        </Field>
      )}
      {form.cadence === 'weekly' && (
        <Field label="Day of the week">
          <select
            value={form.dayOfWeek}
            onChange={(e) => onChange({ dayOfWeek: e.target.value })}
            style={inputStyle}
          >
            {COMMITMENT_DOW_LABEL.map((label, index) => (
              <option key={label} value={String(index)}>{label}</option>
            ))}
          </select>
        </Field>
      )}
    </div>
  );
}

function usePolicy(productId: string): { policy: ActiveCommitmentPolicy | null; message: string | null } {
  const [policy, setPolicy] = useState<ActiveCommitmentPolicy | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPolicy(null);
    setMessage(null);
    if (!productId) return undefined;
    commitmentsService.getActiveProductPolicy(productId)
      .then((p) => { if (!cancelled) setPolicy(p); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const none = axios.isAxiosError(e) && e.response?.status === 404;
        setMessage(none
          ? 'This series does not offer contribution plans yet. You can still send a transfer at any time.'
          : getApiErrorMessage(e, 'Could not load the plan options for this series.'));
      });
    return () => { cancelled = true; };
  }, [productId]);

  return { policy, message };
}

/** The series' minimum first contribution (S-54), shown only while it binds the client: the
 *  server sends it until they hold capital in the series. Below it the server refuses the plan,
 *  so the client is told before they send it and how to clear it. */
export function EntryMinimum({
  product,
  amount,
}: Readonly<{ product: FundableProduct | undefined; amount: string }>): JSX.Element | null {
  const minimum = product?.minimum_first_contribution;
  if (!minimum) return null;
  return (
    <>
      <p className="text-xs" style={{ color: colors.muted }}>
        {`Minimum first contribution: ${formatMoney(minimum, product?.currency_code ?? null)}`}
      </p>
      {isBelowMinimum(amount, minimum) && (
        <Notice tone="info">
          Your first contribution to this series has to reach this minimum. Enter that amount or more.
        </Notice>
      )}
    </>
  );
}

export default function ContributionPlanForm({
  clientId,
  products,
}: Readonly<{ clientId: string; products: FundableProduct[] }>): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState(products[0]?.product_id ?? '');
  const { policy, message } = usePolicy(open ? productId : '');
  const selected = products.find((p) => p.product_id === productId);
  const [form, setForm] = useState<PlanForm>(defaultPlanForm(null, productId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewRequired, setReviewRequired] = useState(false);
  // A refusal that sends the client to their Account Executive (S-43): shown with the call.
  const [executiveRefusal, setExecutiveRefusal] = useState<string | null>(null);

  useEffect(() => {
    setForm(defaultPlanForm(policy, productId));
    setError(null);
    setReviewRequired(false);
    setExecutiveRefusal(null);
  }, [policy, productId]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-4 py-2 rounded-lg text-sm font-semibold"
        style={{ background: colors.ink, color: colors.surface }}
      >
        Set up a contribution plan
      </button>
    );
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!policy) return;
    const problem = planProblem(policy, form);
    setError(problem);
    setReviewRequired(false);
    setExecutiveRefusal(null);
    if (problem) return;
    setSubmitting(true);
    try {
      const { id } = await commitmentsService.draft(draftBody(clientId, form));
      const created = await commitmentsService.get(id);
      navigate(`/client/contracts/${created.contract_case_id}`);
    } catch (err: unknown) {
      const code = getApiErrorCode(err);
      if (code === REVIEW_REQUIRED_CODE) {
        setReviewRequired(true);
      } else if (channelsToExecutive(code)) {
        setExecutiveRefusal(getApiErrorMessage(err, 'Could not set up your plan. Please try again.'));
      } else {
        setError(getApiErrorMessage(err, 'Could not set up your plan. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => { void submit(e); }}
      className="rounded-2xl p-5 grid gap-3"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <Field label="Series">
        <select value={productId} onChange={(e) => setProductId(e.target.value)} style={inputStyle}>
          {products.map((p) => (
            <option key={p.product_id} value={p.product_id}>{p.product_name}</option>
          ))}
        </select>
      </Field>

      {message && <Notice tone="info">{message}</Notice>}

      {policy && (
        <>
          <Field label={`Amount (${selected?.currency_code ?? ''})`}>
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              min={policy.min_amount}
              max={policy.max_amount ?? undefined}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: limitToCents(e.target.value) })}
              style={inputStyle}
            />
          </Field>
          <EntryMinimum product={selected} amount={form.amount} />
          <ScheduleFields policy={policy} form={form} onChange={(patch) => setForm({ ...form, ...patch })} />
          <p className="text-xs" style={{ color: colors.muted }}>
            Next you will review and sign your plan. It starts once it has been confirmed.
          </p>
          {error && <Notice tone="danger">{error}</Notice>}
          {reviewRequired && <ReviewRequiredNotice productId={productId} />}
          {executiveRefusal && (
            <ExecutiveRefusal message={executiveRefusal} productId={productId} context={CALL_CONTEXT.contributionPlan} />
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: colors.ink, color: colors.surface }}
            >
              {submitting ? 'Setting up…' : 'Continue to sign'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ color: colors.inkSoft, border: `1px solid ${colors.border}` }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </form>
  );
}
